//@flow
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';

import { clientSelector } from 'common/redux/selectors/queryParamsSelectors';
import { ClientSelectors } from 'common/redux/selectors/clientSelector';
import { userSelector } from 'common/redux/selectors/userSelector';
import ZoneMapSelectors from 'common/redux/selectors/zoneMap';

import theme from './ZoneMapEditViewContainer.scss';
import { withRouter } from 'common/components/core/router';
import ZoneMapEditView from 'common/components/zoneMap/ZoneMapEditView';
import {
    compose,
    lifecycle,
    withHandlers,
    withStateHandlers,
    withPropsOnChange
} from 'recompose';

import { ZoneMapGeometryStatusEnum } from 'common/enums/ZoneMapGeometryStatus';
import { ZoneMapGeometryTypeEnum } from 'common/enums/ZoneMapGeometryType';

import { ZoneMapGeometry } from 'common/models/internal/ZoneMapGeometry';
import { ZoneMapFolder } from 'common/models/internal/ZoneMapFolder';
import { VerificationStatus } from 'common/models/internal/VerificationStatus';
import ZoneMapActions from 'common/redux/actions/zoneMap';
import { translate } from 'react-i18next';
import { Map, List } from 'immutable';
import { zoneMapColors } from 'common/enums/ColorEnum';
import GlobalUtils from 'common/utils/GlobalUtils';
import DataUtils from 'common/utils/DataUtils';

class ZoneMapEditViewContainer extends PureComponent {
    render() {
        return (
            <div className={theme.container}>
                <ZoneMapEditView {...this.props} />
            </div>
        );
    }
}

const mapStateToProps = (state, props) => {
    const clientId = clientSelector(state, props);
    const canVerify = ZoneMapSelectors.canPersonnelVerifyEntities(state, props);
    const isMapLoading = ZoneMapSelectors.isSelectedMapLoading(state, props);
    const availableRiders = userSelector(state, props);
    const defaultCountryCenter = ClientSelectors.getCountryCenterFromLocaleSettings(
        state,
        props
    );
    return {
        clientId,
        canVerify,
        isMapLoading,
        availableRiders,
        defaultCountryCenter
    };
};

const actions = {
    addZoneMap: ZoneMapActions.createEntity,
    updateZoneMap: ZoneMapActions.addEntity,
    verifyZones: ZoneMapActions.verifyEntities
};

const sortGeometries = (geometries, folders) => {
    let geometryMap = { grouped: Map(), ungrouped: List() };
    let sections = List();
    geometries.forEach(geometry => {
        const { directoryId } = geometry;
        if (directoryId) {
            const { grouped: existingGrouped } = geometryMap;
            const groupedGeometries = existingGrouped.get(directoryId);
            const grouped = groupedGeometries
                ? groupedGeometries.push(geometry)
                : List().push(geometry);
            const foldersMap = existingGrouped.set(directoryId, grouped);
            geometryMap = { ...geometryMap, grouped: foldersMap };
        } else {
            const ungrouped = geometryMap.ungrouped.push(geometry);
            geometryMap = { ...geometryMap, ungrouped };
        }
    });

    folders.forEach(folder => {
        const { id } = folder;
        const { grouped: existingGrouped } = geometryMap;
        if (existingGrouped.has(id)) {
            sections = sections.concat(existingGrouped.get(id));
        }
    });
    return sections.concat(geometryMap.ungrouped);
};

const ZoneMapStateHandlers = compose(
    withStateHandlers(
        ({ initialZoneMap }) => {
            return {
                isMapModified: false,
                isMapToBeVerified: false,
                selectedZoneId: null,
                hoveredGeometryIds: [],
                layerRefs: Map(),
                selectedGroupId: null,
                hoveredGroupId: null,
                zoneMap: initialZoneMap,
                bounds: Map(),
                namedZones: false
            };
        },
        {
            setMap: () => (zoneMap, options = {}) => {
                let newState = { zoneMap };
                if (options.sort) {
                    const { geometries, folders } = zoneMap;
                    const updatedGeometries = sortGeometries(
                        geometries,
                        folders
                    );
                    newState = {
                        ...newState,
                        zoneMap: zoneMap.setGeometries(updatedGeometries)
                    };
                }
                return newState;
            },
            setMapCenter: () => center => {
                return {
                    mapCenter: center
                };
            },
            setMapModified: () => () => {
                return {
                    isMapModified: true
                };
            },
            setMapUnmodified: () => () => {
                return {
                    isMapModified: false
                };
            },
            setMapToBeVerified: () => () => {
                return {
                    isMapToBeVerified: true
                };
            },
            resetMapVerification: () => () => {
                return {
                    isMapToBeVerified: false
                };
            },
            onUpdateGeometry: ({ zoneMap }) => (geometry, options = {}) => {
                const updatedMap = zoneMap.updateGeometry({ geometry });
                let newState = {
                    zoneMap: updatedMap
                };
                newState = options.verification
                    ? { ...newState, isMapToBeVerified: true }
                    : { ...newState, isMapModified: true };

                if (options.sort) {
                    const { geometries, folders } = updatedMap;
                    const updatedGeometries = sortGeometries(
                        geometries,
                        folders
                    );
                    newState = {
                        ...newState,
                        zoneMap: updatedMap.setGeometries(updatedGeometries)
                    };
                }
                return newState;
            },
            setSelectedZoneId: () => selectedZoneId => {
                return {
                    selectedZoneId
                };
            },
            resetActiveZone: () => () => {
                return {
                    selectedZoneId: null
                };
            },
            setHoveredGeometryIds: () => geometryIds => {
                return {
                    hoveredGeometryIds: geometryIds
                };
            },
            setSelectedGroupId: () => selectedGroupId => {
                return {
                    selectedGroupId
                };
            },
            setHoveredGroupId: () => directoryId => {
                return {
                    hoveredGroupId: directoryId
                };
            },
            resetActiveGroup: () => () => {
                return {
                    selectedGroupId: null
                };
            },
            setSelectedZoneRef: () => selectedZoneRef => {
                return {
                    selectedZoneRef
                };
            },
            resetSelectedZoneRef: () => () => {
                return {
                    selectedZoneRef: null
                };
            },
            setLayerRef: ({ layerRefs, bounds }) => ({
                geometry,
                layer,
                bounds: zoneBounds
            }) => {
                const id = geometry.id.getId();
                return {
                    layerRefs: layerRefs.set(id, layer),
                    bounds: bounds.set(id, zoneBounds)
                };
            },
            removeLayerRef: ({ layerRefs, bounds }) => ({ id }) => {
                const geometryId = id.getId();

                return {
                    layerRefs: layerRefs.delete(geometryId),
                    bounds: bounds.delete(geometryId)
                };
            },
            showZoneLabels: () => () => {
                return {
                    namedZones: true
                };
            },
            hideZoneLabels: () => () => {
                return {
                    namedZones: false
                };
            }
        }
    )
);

const SelectionHandlers = compose(
    withHandlers({
        setActiveZone: ({ setSelectedZoneId }) => zone => {
            const { id } = zone;
            setSelectedZoneId(id.getId());
        },
        setHoveredGeometries: ({ setHoveredGeometryIds }) => geometries => {
            const geometryIds = geometries.map(geometry => geometry.id.getId());
            setHoveredGeometryIds(geometryIds);
        },
        resetHoveredGeometries: ({
            setHoveredGeometryIds,
            setHoveredGroupId
        }) => () => {
            setHoveredGeometryIds([]);
            setHoveredGroupId(null);
        },
        setActiveGroup: ({ setSelectedGroupId }) => group => {
            const { id } = group;
            setSelectedGroupId(id);
        },
        setHoveredGroup: ({ setHoveredGroupId }) => id => {
            setHoveredGroupId(id);
        },
        resetHoveredGroup: ({ setHoveredGroupId }) => () => {
            setHoveredGroupId(null);
        },
        enableLayerPopup: ({ layerRefs }) => geometry => {
            const { id } = geometry;
            const layerRef = layerRefs.get(id.getId());
            layerRef && layerRef.openPopup(layerRef, layerRef._popup._latlng);
        },
        toggleLabels: ({
            namedZones,
            hideZoneLabels,
            showZoneLabels
        }) => () => {
            namedZones ? hideZoneLabels() : showZoneLabels();
        }
    }),
    withHandlers({
        handleMapNameSave: ({ zoneMap, setMap, setMapModified }) => name => {
            setMap(zoneMap.setName(name));
            setMapModified();
            return Promise.resolve();
        },
        handleZoneClick: ({ setActiveZone, setSelectedGroupId }) => ({
            zone
        }) => {
            setActiveZone(zone);
            setSelectedGroupId(zone.directoryId);
        },
        handleEntityCardClick: ({
            setActiveZone,
            resetActiveGroup,
            selectedGroupId
        }) => ({ geometry }) => {
            const { directoryId } = geometry;
            if (selectedGroupId !== directoryId) {
                resetActiveGroup();
            }
            setActiveZone(geometry);
        },
        handleGeometryHover: ({ setHoveredGeometries, setHoveredGroupId }) => ({
            geometry
        }) => {
            const { directoryId } = geometry;
            setHoveredGeometries([geometry]);
            setHoveredGroupId(directoryId);
        },
        handleGeometryCardHover: ({
            setHoveredGeometries,
            setHoveredGroupId
        }) => ({ geometry }) => {
            const { directoryId } = geometry;
            setHoveredGeometries([geometry]);
            setHoveredGroupId(directoryId);
        },
        createZone: ({
            t,
            zoneMap,
            onUpdateGeometry,
            setActiveZone,
            setInvalidZoneDialogActive
        }) => ({ layer, type, parentId }) => {
            const { geometries } = zoneMap;
            let newZone;
            let vertices;
            const colors = Object.values(zoneMapColors);
            const randomColorIndex = GlobalUtils.getRandomInt(
                0,
                colors.length - 1
            );
            const randomColor = colors[randomColorIndex].code;
            let isValid = true;

            if (type === ZoneMapGeometryTypeEnum.Polygon) {
                vertices = layer.getLatLngs()[0];
                if (vertices.length >= 3) {
                    newZone = {
                        geometry: {
                            polygon: {
                                type: ZoneMapGeometryTypeEnum.Polygon,
                                vertices
                            }
                        }
                    };
                } else {
                    isValid = false;
                }
            } else if (type === ZoneMapGeometryTypeEnum.Circle) {
                newZone = {
                    geometry: {
                        circle: {
                            center: layer.getLatLng(),
                            radius: layer.getRadius()
                        }
                    }
                };
            } else if (type === ZoneMapGeometryTypeEnum.Polyline) {
                vertices = layer.getLatLngs();
                newZone = {
                    geometry: {
                        polygon: {
                            type: ZoneMapGeometryTypeEnum.Polyline,
                            vertices
                        }
                    }
                };
            }

            if (isValid) {
                let directoryId = null;
                if (parentId) {
                    const parentGeometry = geometries.find(
                        geometry => geometry.id.getId() === parentId
                    );
                    directoryId = parentGeometry.directoryId;
                    onUpdateGeometry(
                        parentGeometry.updateInnerPolygons(vertices)
                    );
                }
                const zoneName =
                    type === ZoneMapGeometryTypeEnum.Polyline
                        ? t('zoneMap.untitledLine')
                        : t('zoneMap.untitledZone');
                newZone = ZoneMapGeometry.fromJS({
                    ...newZone,
                    name: zoneName,
                    id: { geometryId: Date.now().toString() },
                    visualProperties: { color: randomColor, opacity: 1 },
                    directoryId
                }).setInternalStatus(ZoneMapGeometryStatusEnum.New);
                onUpdateGeometry(newZone);
                setActiveZone(newZone);
            } else {
                setInvalidZoneDialogActive();
            }

            layer.remove();
        },
        handleZoneDelete: ({
            setMap,
            zoneMap,
            removeLayerRef,
            setMapModified,
            resetActiveZone
        }) => ({ zoneId }) => {
            if (!zoneId && !zoneMap) {
                return;
            }

            const { geometries } = zoneMap;
            //Check for Hidden since Leaflet emits remove event on hide
            const updatedGeometries = geometries.map(geometry => {
                return !geometry.isHidden() &&
                    geometry.id.getId() === zoneId.getId()
                    ? geometry.setInternalStatus(
                          ZoneMapGeometryStatusEnum.Inactive
                      )
                    : geometry;
            });
            setMapModified();
            setMap(zoneMap.setGeometries(updatedGeometries));
            resetActiveZone();
            removeLayerRef({ id: zoneId });
        },
        handleZoneVisibilityToggle: ({ onUpdateGeometry }) => ({
            geometry
        }) => {
            const updatedStatus = !geometry.isHidden()
                ? ZoneMapGeometryStatusEnum.Hidden
                : undefined;
            onUpdateGeometry(geometry.setInternalStatus(updatedStatus));
        },
        ungroupZone: ({ onUpdateGeometry }) => ({ zone }) => {
            onUpdateGeometry(zone.setDirectoryId(null), { sort: true });
        },
        handlePlaceSearch: ({
            zoneMap,
            setActiveZone,
            setActiveGroup,
            setHoveredGroup,
            enableLayerPopup,
            clientId,
            onUpdateGeometry,
            setHoveredGeometries
        }) => (lat, lng, description, { target, type } = {}) => {
            if (target) {
                if (type === 'geometry') {
                    setActiveZone(target);
                    setActiveGroup({ id: target.directoryId });
                    enableLayerPopup(target);
                } else if (type === 'folder') {
                    const { geometries } = zoneMap;
                    const { id } = target;
                    const geometriesIncluded = geometries.filter(
                        geometry => geometry.directoryId === id
                    );
                    setHoveredGroup(target.id);
                    setHoveredGeometries(geometriesIncluded);
                }

                return;
            }
            const searchMarker = ZoneMapGeometry.fromJS({
                geometry: {
                    polygon: {
                        type: ZoneMapGeometryTypeEnum.Point,
                        vertices: [{ lat, lng }]
                    }
                },
                name: description.split(',')[0],
                id: { clientId, geometryId: Date.now() },
                description
            }).setInternalStatus(ZoneMapGeometryStatusEnum.Search);
            setActiveZone(searchMarker);
            onUpdateGeometry(searchMarker);
        },
        handlePopupClose: ({ onUpdateGeometry }) => ({ zone }) => {
            if (zone.getInternalStatus() === ZoneMapGeometryStatusEnum.New) {
                onUpdateGeometry(
                    zone.setInternalStatus(ZoneMapGeometryStatusEnum.Active)
                );
            }
        },
        handleReorderZone: ({ zoneMap, setMap, setMapModified }) => ({
            geometry: selectedGeometry,
            folderId,
            index,
            removeFolder
        }) => {
            const { geometries } = zoneMap;
            const activeGeometries = geometries.filter(
                geometry =>
                    geometry.getInternalStatus() !==
                        ZoneMapGeometryStatusEnum.Inactive &&
                    (geometry.getType() !== ZoneMapGeometryTypeEnum.Point ||
                        geometry.getInternalStatus() ===
                            ZoneMapGeometryStatusEnum.Search)
            );
            const currentIndex = activeGeometries.findIndex(
                geometry => geometry.id.getId() === selectedGeometry.id.getId()
            );
            if (!DataUtils.isEmpty(currentIndex) && !DataUtils.isEmpty(index)) {
                let updatedGeometries = activeGeometries;
                if (!folderId) {
                    updatedGeometries = activeGeometries
                        .delete(currentIndex)
                        .insert(
                            index,
                            removeFolder
                                ? selectedGeometry.setDirectoryId(null)
                                : selectedGeometry
                        );
                    setMap(zoneMap.setGeometries(updatedGeometries), {
                        sort: true
                    });
                } else {
                    const updatedGeometry = selectedGeometry.setDirectoryId(
                        folderId
                    );

                    const updatedGeometries = activeGeometries
                        .delete(currentIndex)
                        .insert(index, updatedGeometry);

                    setMap(zoneMap.setGeometries(updatedGeometries), {
                        sort: true
                    });
                }
                setMapModified();
            }
        }
    })
);

const FolderHandlers = compose(
    withHandlers({
        handleFolderCardHover: ({ setHoveredGroup, setHoveredGeometries }) => ({
            folder,
            geometriesIncluded = []
        }) => {
            const { id } = folder;
            setHoveredGeometries(geometriesIncluded);
            setHoveredGroup(id);
        },
        handleFolderCardBlur: ({ resetHoveredGeometries }) => () => {
            resetHoveredGeometries();
        },
        handleFolderCardClick: ({
            setActiveGroup,
            resetActiveGroup,
            selectedGroupId
        }) => ({ folder }) => {
            if (selectedGroupId === folder.id) {
                resetActiveGroup();
                return;
            }
            setActiveGroup(folder);
        },
        handleAddFolder: ({ t, zoneMap, setMap }) => () => {
            const createdOn = Date.now().toString();
            const newFolder = ZoneMapFolder.fromJS({
                id: createdOn,
                createdOn,
                name: t('zoneMap.untitled')
            }).setInternalStatus(ZoneMapGeometryStatusEnum.New);

            setMap(zoneMap.updateFolder({ folder: newFolder }));
        },
        handleFolderNameUpdate: ({
            zoneMap,
            setMap,
            setMapModified
        }) => folder => {
            setMap(zoneMap.updateFolder({ folder }));
            setMapModified();
            return Promise.resolve();
        },
        handleFolderVisibilityToggle: ({ zoneMap, setMap }) => ({ folder }) => {
            const { id } = folder;
            const status = folder.getInternalStatus();
            const updatedStatus =
                status !== ZoneMapGeometryStatusEnum.Hidden
                    ? ZoneMapGeometryStatusEnum.Hidden
                    : undefined;
            const { geometries, folders } = zoneMap;
            const updatedFolders = folders.map(folder => {
                return folder.id === id
                    ? folder.setInternalStatus(updatedStatus)
                    : folder;
            });
            const updatedGeometries = geometries.map(geometry => {
                return geometry.directoryId === id
                    ? geometry.setInternalStatus(updatedStatus)
                    : geometry;
            });
            setMap(
                zoneMap
                    .setFolders(updatedFolders)
                    .setGeometries(updatedGeometries)
            );
        },
        handleFolderDelete: ({ zoneMap, setMap, setMapModified }) => ({
            folder: deletedFolder
        }) => {
            const { folders, geometries } = zoneMap;
            const { id } = deletedFolder;
            const updatedFolders = folders.filter(folder => folder.id !== id);
            const updatedGeometries = geometries.map(geometry => {
                return geometry.directoryId === id
                    ? geometry.setInternalStatus(
                          ZoneMapGeometryStatusEnum.Inactive
                      )
                    : geometry;
            });
            setMap(
                zoneMap
                    .setFolders(updatedFolders)
                    .setGeometries(updatedGeometries)
            );
            setMapModified();
        }
    })
);
const ApiHandlers = compose(
    withHandlers({
        onVerifyFailure: () => () => {},
        onZoneMapUpdateFailure: () => () => {}
    }),
    withHandlers({
        saveZoneMap: ({ clientId, addZoneMap, updateZoneMap }) => ({
            zoneMap,
            onSuccess,
            onFailure
        }) => {
            const { id: mapId } = zoneMap;
            const isUpdate = mapId ? true : false;
            const params = isUpdate ? { clientId, mapId } : { clientId };
            const saveAction = isUpdate ? updateZoneMap : addZoneMap;
            const body = zoneMap;
            const options = { onSuccess, onFailure };

            saveAction(params, body, options);
        },
        handleVerifyZones: ({ clientId, verifyZones }) => (
            response,
            body,
            options
        ) => {
            const { version, id: mapId } = response;
            const params = { clientId, mapId, versionId: version };
            verifyZones(params, body, options);
        }
    })
);

const ZoneMapHandlers = compose(
    withHandlers({
        handleZoneMapSave: ({
            isMapModified,
            isMapToBeVerified,
            canVerify,
            saveZoneMap,
            handleVerifyZones,
            history,
            clientId
        }) => ({ map: zoneMap, onSuccess }) => {
            const { geometries, folders } = zoneMap;

            const updatedGeometries = geometries.filterNot(
                geometry =>
                    geometry.getInternalStatus() ===
                        ZoneMapGeometryStatusEnum.Inactive ||
                    geometry.getInternalStatus() ===
                        ZoneMapGeometryStatusEnum.Search
            );
            const updatedFolders = folders.filter(
                folder =>
                    folder.getInternalStatus() !==
                    ZoneMapGeometryStatusEnum.Inactive
            );

            const updatedZoneMap = zoneMap
                .setGeometries(updatedGeometries)
                .setFolders(updatedFolders);

            const zonesToBeVerified = zoneMap.geometries.map(geometry => ({
                id: geometry.id.getId(),
                verified: geometry.isVerified()
            }));

            isMapModified
                ? saveZoneMap({
                      zoneMap: updatedZoneMap,
                      onSuccess: response => {
                          canVerify &&
                              handleVerifyZones(response, zonesToBeVerified);
                          const { id, version } = response;
                          const { version: prevVersion } = updatedZoneMap;
                          if (
                              response &&
                              updatedZoneMap &&
                              (!prevVersion || version > prevVersion)
                          ) {
                              history.push(
                                  `/client/${clientId}/entity/map/${id}`
                              );
                          }
                          onSuccess && onSuccess();
                      },
                      onFailure: () => {}
                  })
                : canVerify &&
                  isMapToBeVerified &&
                  handleVerifyZones(updatedZoneMap, zonesToBeVerified, {
                      onSuccess
                  });
        }
    })
);

const VerifyHandlers = compose(
    withHandlers({
        handleZoneVerifyToggle: ({ onUpdateGeometry }) => ({ geometry }) => {
            const updatedVerificationStatus = VerificationStatus.fromJS({
                verified: geometry.isVerified() ? false : true
            });

            onUpdateGeometry(
                geometry.setVerificationStatus(updatedVerificationStatus),
                { verification: true }
            );
        },
        handleFolderVerifyToggle: ({
            setMap,
            setMapToBeVerified,
            zoneMap
        }) => ({ folder, isVerified }) => {
            const { geometries } = zoneMap;
            const updatedVerificationStatus = VerificationStatus.fromJS({
                verified: isVerified ? false : true
            });
            const updatedGeometries = geometries.map(geometry => {
                return geometry.getInternalStatus() !==
                    ZoneMapGeometryStatusEnum.Inactive &&
                    geometry.directoryId === folder.id
                    ? geometry.setVerificationStatus(updatedVerificationStatus)
                    : geometry;
            });

            setMapToBeVerified();
            setMap(zoneMap.setGeometries(updatedGeometries));
        },
        handleMapVerifyToggle: ({
            setMap,
            setMapToBeVerified,
            zoneMap,
            isMapVerified
        }) => () => {
            const { geometries } = zoneMap;
            const updatedVerificationStatus = VerificationStatus.fromJS({
                verified: isMapVerified ? false : true
            });
            const updatedGeometries = geometries.map(geometry => {
                return geometry.getInternalStatus() !==
                    ZoneMapGeometryStatusEnum.Inactive
                    ? geometry.setVerificationStatus(updatedVerificationStatus)
                    : geometry;
            });
            setMapToBeVerified();
            setMap(zoneMap.setGeometries(updatedGeometries));
        }
    })
);

const DialogHandlers = compose(
    withStateHandlers(
        () => ({
            isInvalidZoneDialogActive: false
        }),
        {
            setInvalidZoneDialogActive: () => () => ({
                isInvalidZoneDialogActive: true
            }),
            setInvalidZoneDialogInactive: () => () => ({
                isInvalidZoneDialogActive: false
            })
        }
    ),
    withHandlers({
        handleBackClick: ({ onBack, isMapModified }) => () => {
            onBack({ isConfirmDialogOpen: isMapModified });
        },
        handleBackSave: ({ zoneMap, handleZoneMapSave }) => (
            name,
            { onSuccess }
        ) => {
            const updatedMap = name ? zoneMap.setName(name) : zoneMap;
            handleZoneMapSave({ map: updatedMap, onSuccess });
        },
        handleBackConfirm: ({ onBack }) => () => {
            onBack({ isConfirmDialogOpen: false, pushRoute: false });
        }
    })
);

const zoneMapSetter = {
    componentDidUpdate(prevProps) {
        const {
            initialZoneMap,
            setMap,
            setMapUnmodified,
            resetMapVerification,
            setMapCenter
        } = this.props;
        const { initialZoneMap: prevMap } = prevProps;

        if (initialZoneMap && initialZoneMap !== prevMap) {
            const {
                geometries: initialGeometries,
                folders: initialFolders,
                defaultCenter
            } = initialZoneMap;
            const updatedGeometries = sortGeometries(
                initialGeometries,
                initialFolders
            );
            if (updatedGeometries && !updatedGeometries.size && defaultCenter) {
                // fallback when zones are not there providing homebase as center of map
                const { lat, lng } = defaultCenter;
                setMapCenter([lat, lng]);
            }
            setMap(initialZoneMap.setGeometries(updatedGeometries));
            setMapUnmodified();
            resetMapVerification();
        }
    }
};

const DerivedProps = withPropsOnChange(['zoneMap'], ({ zoneMap }) => {
    const { geometries = [] } = zoneMap || {};
    const isMapVerified =
        geometries &&
        geometries.filter(
            geometry =>
                geometry.getInternalStatus() !==
                ZoneMapGeometryStatusEnum.Inactive
        ).size
            ? geometries.reduce((isVerified, geometry) => {
                  return (
                      isVerified &&
                      !!(
                          geometry.getInternalStatus() ===
                              ZoneMapGeometryStatusEnum.Inactive ||
                          geometry.getInternalStatus() ===
                              ZoneMapGeometryStatusEnum.Search ||
                          geometry.isVerified()
                      )
                  );
              }, true)
            : false;
    return {
        isMapVerified
    };
});

const ZoneMapEditContainer = compose(
    withRouter,
    connect(mapStateToProps, actions),
    translate('entityManagement'),
    ZoneMapStateHandlers,
    DerivedProps,
    VerifyHandlers,
    ApiHandlers,
    ZoneMapHandlers,
    DialogHandlers,
    SelectionHandlers,
    FolderHandlers,
    lifecycle(zoneMapSetter)
)(ZoneMapEditViewContainer);

export default ZoneMapEditContainer;
